import { isSessionId } from "./sheets-wizard-session";

/**
 * MOSA-467. The three URLs one wizard run produces. Kept pure so the specs can
 * check them without a browser.
 *
 * The session id is the one value that must survive every hop: the wizard URL,
 * the sign-in `next=` and the OAuth return path. The link travels with it only
 * while the session does not exist yet.
 */

/** `#/connect/sheets?session=S`. */
export const wizardPath = (session: string): string =>
  `/connect/sheets?session=${encodeURIComponent(session)}`;

/** The first wizarding URL, for a browser that has no session yet. */
export const wizardEntryPath = (link?: string | null): string =>
  link ? `/connect/sheets?sheet=${encodeURIComponent(link)}` : "/connect/sheets";

/** The sign-in `next=` value: the session when there is one, else the link. */
export function wizardNextPath(args: {
  session?: string | null;
  link?: string | null;
}): string {
  if (isSessionId(args.session)) {
    return wizardPath(args.session as string);
  }
  return wizardEntryPath(args.link);
}

/**
 * The `session` and `sheet` values to keep in the address bar. A session in the
 * URL wins. The wizard removes `sheet` then, because the session already holds
 * the link.
 * `google_sheets`, `connectionId` and `link_error` are one-shot and never kept.
 */
export function wizardUrlPatch(args: {
  session?: string | null;
  link?: string | null;
}): Record<string, string | undefined> {
  const session = isSessionId(args.session) ? (args.session as string) : undefined;
  return {
    session,
    sheet: session ? undefined : args.link || undefined,
  };
}

/**
 * MOSA-468. The Picker tab lands on the wizard itself, with `picker=new` so the
 * wizard knows it must open the Picker there and not run the read. It keeps the
 * session id, so the tab and the first tab share one run of the wizard.
 */
export const pickerTabPath = (session?: string | null): string =>
  isSessionId(session)
    ? `/connect/sheets?session=${encodeURIComponent(session as string)}&picker=new`
    : "/connect/sheets?picker=new";

/** The `picker=new` marker the Picker tab uses. */
export const PICKER_TAB_PARAM = "picker";
